import { useState } from "react";
import { Layout } from "./LegalPage";
import { Copy, Check, ExternalLink, FileCode2 } from "lucide-react";

const CONTRACT = "0x47935d80e63eddf23f351db33b2363b6925f1530";

function getLang() {
  const supported = ["it", "en", "de", "fr"];
  if (typeof localStorage !== "undefined") {
    const saved = localStorage.getItem("ustp-lang");
    if (supported.includes(saved)) return saved;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const code = navigator.language.toLowerCase().slice(0, 2);
    return supported.includes(code) ? code : "en";
  }
  return "it";
}

const copy = {
  it: {
    title: "Smart Contract",
    updated: "Rete Polygon (MATIC)",
    intro:
      "Di seguito pubblichiamo l'indirizzo ufficiale dello smart contract del token USTP. Il contratto è pubblicato sulla rete Polygon.",
    networkLabel: "Rete",
    network: "Polygon (MATIC)",
    addressLabel: "Indirizzo del contratto",
    copy: "Copia indirizzo",
    copied: "Copiato!",
    explorer: "Visualizza su PolygonScan",
    warning:
      "Verifica sempre che l'indirizzo del contratto corrisponda esattamente a quello pubblicato su questa pagina ufficiale prima di qualsiasi interazione. Diffida da indirizzi diversi.",
  },
  en: {
    title: "Smart Contract",
    updated: "Polygon network (MATIC)",
    intro:
      "Below we publish the official smart contract address of the USTP token. The contract is deployed on the Polygon network.",
    networkLabel: "Network",
    network: "Polygon (MATIC)",
    addressLabel: "Contract address",
    copy: "Copy address",
    copied: "Copied!",
    explorer: "View on PolygonScan",
    warning:
      "Always verify that the contract address matches exactly the one published on this official page before any interaction. Beware of different addresses.",
  },
};

export default function SmartContractPage() {
  const t = copy[getLang()];
  const [done, setDone] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTRACT);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch {
      /* clipboard non disponibile */
    }
  };

  return (
    <Layout title={t.title} updated={t.updated}>
      <div data-testid="smart-contract-content" className="space-y-7">
        <div className="flex items-center gap-3 text-sky-700">
          <FileCode2 size={22} />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">USTP · ERC-20</span>
        </div>

        <p>{t.intro}</p>

        <div className="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.networkLabel}</p>
          <p data-testid="smart-contract-network" className="mt-1 flex items-center gap-2 text-base font-semibold text-slate-900">
            <span className="h-2.5 w-2.5 rounded-full bg-purple-600" />
            {t.network}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.addressLabel}</p>
          <p
            data-testid="smart-contract-address"
            className="mt-2 break-all font-mono text-sm sm:text-base font-semibold text-slate-900"
          >
            {CONTRACT}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="smart-contract-copy-btn"
              onClick={onCopy}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-600"
            >
              {done ? <Check size={16} /> : <Copy size={16} />}
              {done ? t.copied : t.copy}
            </button>
            <a
              href={`https://polygonscan.com/address/${CONTRACT}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="smart-contract-explorer-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-sky-500 hover:text-sky-600"
            >
              <ExternalLink size={16} />
              {t.explorer}
            </a>
          </div>
        </div>

        <p className="text-xs leading-relaxed text-slate-400">{t.warning}</p>
      </div>
    </Layout>
  );
}
